import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { TokenResponse } from '../../../shared/api/contracts/styleTransferSchemas';
import { useSessionStore } from '../../style-transfer/state/sessionStore';
import { createTTLJSONStorage } from '../../../shared/storage/createTTLJSONStorage';

export type AuthStatus = 'anonymous' | 'authenticating' | 'authenticated';
export type AuthMode = 'login' | 'register' | 'forgotPassword' | 'voucher';

interface AuthDraftState {
  mode: AuthMode;
  email: string;
  password: string;
  confirmPassword: string;
  username: string;
  hasAgreed: boolean;
  voucherCode: string;
  referralCode: string;
}

const createInitialDraft = (): AuthDraftState => ({
  mode: 'login',
  email: '',
  password: '',
  confirmPassword: '',
  username: '',
  hasAgreed: false,
  voucherCode: '',
  referralCode: ''
});

export interface AuthState {
  status: AuthStatus;
  userEmail: string | null;
  userName: string | null;
  error: string | null;
  draft: AuthDraftState;
  setAuthenticating: () => void;
  setAuthenticated: (email: string, token: TokenResponse, userName?: string | null) => void;
  setError: (message: string | null) => void;
  signOut: () => void;
  setDraftMode: (mode: AuthMode) => void;
  updateDraft: (patch: Partial<AuthDraftState>) => void;
  resetDraft: () => void;
}

const AUTH_PERSIST_KEY = 'photo-studio-auth';
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
type AuthPersistedState = Pick<AuthState, 'status' | 'userEmail' | 'userName'>;

export const useAuthStore = create<AuthState>()(
  persist<AuthState, [], AuthPersistedState>(
    (set) => ({
      status: 'anonymous',
      userEmail: null,
      userName: null,
      error: null,
      draft: createInitialDraft(),
      setAuthenticating: () => set({ status: 'authenticating', error: null }),
      setAuthenticated: (email, token, userName) => {
        useSessionStore.getState().setToken(token);
        set({
          status: 'authenticated',
          userEmail: email,
          userName: userName ?? null,
          error: null,
          draft: createInitialDraft()
        });
      },
      setError: (message) =>
        set((state) => {
          const isClearingError = message === null || message === undefined;
          return {
            error: message,
            status: isClearingError ? state.status : 'anonymous',
            userEmail: isClearingError ? state.userEmail : null,
            userName: isClearingError ? state.userName : null,
            draft: { ...state.draft }
          };
        }),
      signOut: () => {
        useSessionStore.getState().logout();
        set({
          status: 'anonymous',
          userEmail: null,
          userName: null,
          error: null,
          draft: createInitialDraft()
        });
      },
      setDraftMode: (mode) =>
        set((state) => ({
          draft: {
            ...state.draft,
            mode
          }
        })),
      updateDraft: (patch) =>
        set((state) => ({
          draft: {
            ...state.draft,
            ...patch
          }
        })),
      resetDraft: () =>
        set((state) => ({
          draft: {
            ...createInitialDraft(),
            mode: state.draft.mode
          }
        }))
    }),
    {
      name: AUTH_PERSIST_KEY,
      storage: createTTLJSONStorage<AuthPersistedState>(ONE_WEEK_MS),
      partialize: (state) => ({
        status: state.status,
        userEmail: state.userEmail,
        userName: state.userName
      })
    }
  )
);

export const selectAuthStatus = (state: AuthState) => state.status;
export const selectAuthError = (state: AuthState) => state.error;
export const selectUserEmail = (state: AuthState) => state.userEmail;
export const selectUserName = (state: AuthState) => state.userName;
export const selectAuthDraft = (state: AuthState) => state.draft;
export const selectAuthDraftMode = (state: AuthState) => state.draft.mode;
